/**
 * Self-healing error logger.
 * All writes go through the SECURITY DEFINER RPC `log_error_event`.
 * Never throws — logging failures are latched to protect the app.
 */
import { supabase } from "@/integrations/supabase/client";

type ErrorClass =
  | "http_404"
  | "http_403"
  | "http_401"
  | "http_500"
  | "rls_denied"
  | "edge_function_error"
  | "react_render_error"
  | "unhandled_rejection"
  | "network_error"
  | "window_error"
  | "self_test";

interface LogInput {
  errorClass: ErrorClass | string;
  route?: string;
  statusCode?: number | null;
  message?: string | null;
  stack?: string | null;
  context?: Record<string, unknown>;
}

// Module-level failure latch — if the logger itself fails, stop hammering it.
let LOGGER_DISABLED = false;
const RECENT_SENDS = new Map<string, number>(); // fingerprint -> ts, throttle 5s
const SESSION_FINGERPRINT_COUNTS = new Map<string, number>();
const CIRCUIT_LIMIT = 3;

function fnv1a(str: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16);
}

function stackSignature(stack?: string | null): string {
  if (!stack) return "";
  // First 3 non-empty frames, stripped of column numbers, to stabilize fingerprint.
  return stack
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .slice(0, 4)
    .join(" | ")
    .replace(/:\d+:\d+/g, "")
    .slice(0, 500);
}

export function computeFingerprint(input: LogInput): string {
  const route = input.route || (typeof window !== "undefined" ? window.location.pathname : "");
  const sig = stackSignature(input.stack);
  return fnv1a(`${input.errorClass}::${route}::${sig || input.message || ""}`);
}

export function circuitTripped(fingerprint: string): boolean {
  return (SESSION_FINGERPRINT_COUNTS.get(fingerprint) || 0) >= CIRCUIT_LIMIT;
}

export async function logError(input: LogInput): Promise<string | null> {
  if (LOGGER_DISABLED) return null;
  try {
    const fingerprint = computeFingerprint(input);
    const now = Date.now();
    const last = RECENT_SENDS.get(fingerprint) || 0;

    SESSION_FINGERPRINT_COUNTS.set(
      fingerprint,
      (SESSION_FINGERPRINT_COUNTS.get(fingerprint) || 0) + 1
    );

    // Debounce identical fingerprints within 5s
    if (now - last < 5000) return null;
    RECENT_SENDS.set(fingerprint, now);

    let memberId: string | null = null;
    try {
      const { data } = await supabase.auth.getUser();
      memberId = data.user?.id ?? null;
    } catch {
      /* ignore */
    }

    const route =
      input.route ||
      (typeof window !== "undefined" ? window.location.pathname + window.location.search : null);

    const { data, error } = await (supabase.rpc as any)("log_error_event", {
      _fingerprint: fingerprint,
      _error_class: input.errorClass,
      _route: route,
      _status_code: input.statusCode ?? null,
      _message: (input.message || "").slice(0, 2000) || null,
      _stack_signature: stackSignature(input.stack) || null,
      _user_role: null,
      _member_id: memberId,
      _context: input.context || {},
    });

    if (error) {
      // If the RPC itself fails, latch off to avoid loops.
      // eslint-disable-next-line no-console
      console.warn("[errorLogger] rpc failed, disabling logger", error);
      LOGGER_DISABLED = true;
      return null;
    }
    return (data as string) || null;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[errorLogger] threw, disabling logger", e);
    LOGGER_DISABLED = true;
    return null;
  }
}

/**
 * Install global window handlers. Safe to call multiple times (no-op after first).
 */
let INSTALLED = false;
export function installGlobalErrorHandlers() {
  if (INSTALLED || typeof window === "undefined") return;
  INSTALLED = true;

  window.addEventListener("error", (event) => {
    void logError({
      errorClass: "window_error",
      message: event.message,
      stack: event.error?.stack,
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason: any = event.reason;
    void logError({
      errorClass: "unhandled_rejection",
      message: typeof reason === "string" ? reason : reason?.message || "unhandled rejection",
      stack: reason?.stack,
    });
  });
}

/**
 * fetch/query error helper: 401 → refresh + retry once; 404 → log; 403 → rls_denied;
 * network → backoff up to 2 retries; circuit breaks at 3 identical failures.
 */
export interface SmartFetchOptions extends RequestInit {
  maxNetworkRetries?: number;
}

export async function smartFetch(input: RequestInfo | URL, init: SmartFetchOptions = {}) {
  const { maxNetworkRetries = 2, ...rest } = init;
  const url = typeof input === "string" ? input : input.toString();
  const route = typeof window !== "undefined" ? window.location.pathname : url;

  let attempt = 0;
  let did401Refresh = false;

  while (true) {
    try {
      const res = await fetch(input, rest);

      if (res.status === 401 && !did401Refresh) {
        did401Refresh = true;
        try {
          await supabase.auth.refreshSession();
        } catch {
          /* ignore */
        }
        continue;
      }

      if (res.status === 404) {
        void logError({ errorClass: "http_404", statusCode: 404, route, message: `404 ${url}` });
      } else if (res.status === 403) {
        void logError({
          errorClass: "rls_denied",
          statusCode: 403,
          route,
          message: `403 ${url}`,
          context: { attempted_route: url },
        });
      } else if (res.status >= 500) {
        void logError({
          errorClass: "http_500",
          statusCode: res.status,
          route,
          message: `${res.status} ${url}`,
        });
      }

      return res;
    } catch (netErr: any) {
      attempt += 1;
      if (attempt > maxNetworkRetries) {
        await logError({
          errorClass: "network_error",
          route,
          message: netErr?.message || "network failure",
          stack: netErr?.stack,
          context: { url },
        });
        throw netErr;
      }
      const backoff = 250 * Math.pow(2, attempt);
      await new Promise((r) => setTimeout(r, backoff));
    }
  }
}
